<template>
    <div>
        <div v-if="loading" class="spinner" ></div>

        <div v-else-if="errors.length" class="alert--error" >
            <p v-for="err in errors" :key="err"> {{ err }} </p>
        </div>

        <form v-if="!loading" class="form" @submit.prevent="save" @keydown.enter.prevent="">
            <slot name="header"></slot>

            <div :class="wrapperClass">
                <div class="form__item">
                    <!-- <i class="form__icon icons icon-user"></i> -->
                    <input v-model="question.description" class="form__input--text" type="text" :class="{ 'has-value': question.description }"/>
                    <label class="form__label">{{$t('forms.survey_template_question.description')}}</label>
                </div>
                <div v-if="!question.id" class="form__item">
                    <!-- <i class="form__icon icons icon-graduation"></i> -->
                    <vue-select label="title" :options="questionTypes" v-model="questionType" @input="fillDefaultOptions" class="form__select" :class="{ 'has-value': question.type }" >
                        <template slot="no-options">{{$t('forms.common.no_results')}}</template>
                    </vue-select>
                    <label class="form__label">{{$t('forms.survey_template_question.type')}}</label>
                </div>

                <div class="form__item--checkbox">
                    <div class="form__checkbox">
                        <input type="checkbox" class="form__checkbox__input" id="survey_template_question_form_required" v-model="question.required" />
                        <label for="survey_template_question_form_required" class="form__checkbox__label">{{$t('forms.survey_template_question.required')}}</label>
                    </div>
                </div>

                <template v-if="questionTypeValue === 'multiple'">
                    <h2>{{$t('forms.survey_template_question.multiple_options')}}:</h2>

                    <div class="form__item--checkbox" v-for="option in question.options" :key="option.label + option.id">
                        <div class="form__checkbox">
                            <input type="checkbox" class="form__checkbox__input" disabled/>
                            <label class="form__checkbox__label">{{option.label}}</label>
                        </div>
                        <i class="form__icon icons icon-trash" @click="deleteOption(option)" style="cursor:pointer;"></i>
                    </div>

                    <div class="form__item--checkbox" >
                        <div class="form__checkbox">
                            <input type="checkbox" class="form__checkbox__input" id="survey_template_question_form_allow_other" v-model="question.allow_other" />
                            <label for="survey_template_question_form_allow_other" class="form__checkbox__label">{{$t('forms.survey_template_question.allow_other')}}</label>
                        </div>
                    </div>
                </template>

                <template v-if="questionTypeValue === 'likert'">
                    <h2>{{$t('forms.survey_template_question.likert_scale_options')}}:</h2>

                    <div class="form__item" v-for="option in question.options" :key="option.label + option.id">
                        <div class="form__radio is-inline">
                            <input type="radio" class="form__radio__input" id="option_rb" disabled />
                            <label class="form__radio__label" for="option_rb">{{option.label}}</label>
                        </div>
                        <i class="form__icon icons icon-trash" @click="deleteOption(option)" style="cursor:pointer;"></i>
                    </div>
                </template>

                <template v-if="questionTypeValue === 'multiple' || questionTypeValue === 'likert'">
                    <div class="row">
                        <div class="col-12">
                            <div class="form__item">
                                <input v-model="newOption" class="form__input--text" type="text" :class="{ 'has-value': newOption}"  @keyup.enter.prevent.stop="addOption"/>
                                <label class="form__label">{{$t('forms.survey_template_question.new_option_label')}}</label>
                            </div>
                        </div>
                        <div class="col-6 offset-6">
                            <button class="button--cta" type="button" @click.prevent="addOption">{{$t('forms.survey_template_question.add_option')}}</button>
                        </div>
                    </div>
                </template>
            </div>

            <slot name="buttons" :isSaveDisabled="isSaveDisabled">
                <div class="form__buttons">
                    <button type="submit" class="form__button button--cta" :class="{ disabled: isSaveDisabled }" :disabled="isSaveDisabled">{{$t('common.save')}}</button>
                </div>
            </slot>
        </form>
    </div>
</template>

<script>
    import Vue from 'vue';
    import { Component, Prop, Watch } from 'vue-property-decorator';
    import SurveyTemplate from '@/models/SurveyTemplate';

    import { SURVEY_TEMPLATE_QUESTION_TYPES, SAVE_SURVEY_TEMPLATE_QUESTION } from '@/store/modules/survey-template';

    @Component
    export default class SurveyTemplateQuestionForm extends Vue {

        @Prop({ type: Array | String, default() {
            return [];
        } })
        wrapperClass;

        @Prop({ type: Object, default() {
            return new Object();
        } })
        question;

        errors = [];

        newOption = '';

        created() {
            this.errors = [];
        }

        get questionTypes() {
            return SURVEY_TEMPLATE_QUESTION_TYPES.map(type => {
                return {value: type, title: this.$t('forms.survey_template_question.types.' + type)};
            });
        }

        // Make sure that the type/title value are consistently returned.
        get questionType() {
            // Return value if it's an object;
            if (this.question.type instanceof Object) return this.question.type;
            // Or find the title if it's only value
            return this.questionTypes.find(t => t.value === this.questionTypeValue);
        }

        set questionType(type) {
            Vue.set(this.question, 'type', type);
        }

        get questionTypeValue() {
            // Return value if it's an object;
            if (this.question.type && this.question.type.value) return this.question.type.value;
            // Or the value if it's
            return this.question.type;
        }

        get questionTypeTitle() {
            // Return value if it's an object;
            if (this.question.type && this.question.type.value) return this.question.type.title;
            // Or find the title if it's only value
            return this.questionTypes.find(t => t.value === this.questionTypeValue).title;
        }


        get isSaveDisabled() {
            return !this.question.description;
        }

        get loading () {
            return this.saveLoading;
        }

        fillDefaultOptions(type) {
            if (type.value === 'likert') {
                const likertDefaultOptions = [
                    { label: this.$t('forms.survey_template_question.likert_scale_default_1') },
                    { label: this.$t('forms.survey_template_question.likert_scale_default_2') },
                    { label: this.$t('forms.survey_template_question.likert_scale_default_3') },
                    { label: this.$t('forms.survey_template_question.likert_scale_default_4') },
                    { label: this.$t('forms.survey_template_question.likert_scale_default_5') }
                ];
                Vue.set(this.question, 'options', likertDefaultOptions);
            } else {
                this.question.options = [];
            }
        }

        addOption() {
            if (!this.question.options) this.question.options = [];
            this.question.options.push({ label: this.newOption });
            this.newOption = '';
        }

        deleteOption(option) {
            const index = this.question.options.indexOf(option);
            this.question.options.splice(index, 1);
        }

        save() {
            this.errors = [];
            this.$store.dispatch(SAVE_SURVEY_TEMPLATE_QUESTION, { question: JSON.parse(JSON.stringify(this.question)) })
                .then(res => this.$emit('saved', res))
                .catch(
                    err => {
                        if (err.status === 422) {
                            this.errors = err.errors;
                        } else {
                            this.errors = [this.$t('common.unknown_error')];
                        }
                    })
                .then(() => this.saveLoading = false);
        }
    }
</script>



// WEBPACK FOOTER //
// src/components/forms/survey-templates/SurveyTemplateQuestionForm.vue